import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
  Alert,
  Badge,
  Spinner,
} from "react-bootstrap";
import { Input } from "reactstrap";
import { serverHost } from "variables/serverdata";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import SimpleHeader from "components/Headers/SimpleHeader";
import Tooltip from "components/ToolTip.js";
const PORT_STATUS = {
  ACTIVE: { label: "Active", variant: "success" },
  PORTED_OUT: { label: "Ported Out", variant: "danger" },
  UNKNOWN: { label: "Unknown", variant: "secondary" },
};

const mnyfmt = (n) => `$${Number(n ?? 0).toFixed(2)}`;
function secondsToHMS(seconds) {
  seconds = Math.floor(seconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

const DIDProfile = () => {
  const navigate = useNavigate();
  const { number } = useParams();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const { id } = useParams();
  const [did, setDid] = useState(null);
  const [e911, setE911] = useState({
    first_name: "",
    last_name: "",
    street_number: "",
    street_name: "",
    suite_number: "",
    city: "",
    province: "",
    postal_code: "",
    other: "",
  });
  const [portStatus, setPortStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portLoading, setPortLoading] = useState(true);
  const [error, setError] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const month = new Date().toLocaleString("default", { month: "long" });
  const labelStyle = {
    fontWeight: "bolder",
    color: isDark ? "#e2e8f0" : "#1e3a5f",
    marginBottom: "0.5rem",
  };

  const valueStyle = {
    color: isDark ? "#cbd5e1" : "#374151",
    fontSize: "1rem",
  };

  const fetchDID = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${serverHost}/fetch_did_cdr/${id}`);
      if (!response.ok)
        throw new Error(`Failed to fetch DID: ${response.status}`);
      const data = await response.json();
      console.log(data);
      setDid(data.did_info);
      setE911(data.e911);
      setPortStatus(data.did_info.status);
      setPortLoading(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateEmergency = async () => {
    setUpdateError("");
    setUpdateSuccess(false);
    try {
      const response = await fetch(`${serverHost}/update_emergency/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          e911: e911,
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to update the 911 information");
      }
      setUpdateSuccess(true);
      await fetchDID();
    } catch (err) {
      console.error(err);
      setUpdateError(err.message || "Failed to update the 911 information");
    }
  };

  const updateE911 = (field, value) => {
    setE911((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Fetch DID details from ThinkTel
  useEffect(() => {
    fetchDID();
  }, [number]);

  const getPortBadge = () => {
    if (portLoading) return <Spinner animation="border" size="sm" />;
    const key = portStatus?.toUpperCase() ?? "UNKNOWN";
    const config = PORT_STATUS[key] ?? PORT_STATUS.UNKNOWN;
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const Field = ({ label, value, required }) => (
    <Form.Group className="mb-4">
      <Form.Label style={labelStyle}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Form.Label>
      <div
        style={{
          ...valueStyle,
          padding: "0.5rem 0.75rem",
          borderRadius: "0.375rem",
          border: `1px solid ${isDark ? "#374151" : "#dee2e6"}`,
          backgroundColor: isDark ? "#111827" : "#f8f9fa",
          minHeight: "2.4rem",
        }}
      >
        {value ?? <span style={{ color: "#9ca3af" }}>—</span>}
      </div>
    </Form.Group>
  );

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading DID...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <SimpleHeader name="DID Profile" parentName="Thinktel" />
      {updateError && (
        <Container className="mt-3">
          <Alert
            variant="danger"
            onClose={() => setUpdateError("")}
            dismissible
          >
            {updateError}
          </Alert>
        </Container>
      )}

      {updateSuccess && (
        <Container className="mt-3">
          <Alert
            variant="success"
            onClose={() => setUpdateSuccess(false)}
            dismissible
          >
            Successfully updated 911 information.
          </Alert>
        </Container>
      )}

      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#e9ecef",
              color: isDark ? "#e2e8f0" : "#1e3a5f",
              borderBottom: "none",
              padding: "1rem 1.5rem",
            }}
            className="d-flex justify-content-between align-items-center"
          >
            <h4 className="mb-0">DID PROFILE</h4>
            <div className="d-flex align-items-center gap-2">
              <span
                style={{
                  color: isDark ? "#9ca3af" : "#6b7280",
                  fontSize: "0.875rem",
                }}
              >
                Port Status:
              </span>
              {getPortBadge()}
            </div>
          </Card.Header>

          <Card.Body className="p-4">
            <Form>
              {/* ── Section 1: DID Information ── */}
              <h4
                style={{
                  fontWeight: 500,
                  color: isDark ? "#93c5fd" : "#1e3a5f",
                  borderBottom: `1px solid ${isDark ? "#374151" : "#dee2e6"}`,
                  paddingBottom: "0.5rem",
                  marginBottom: "1.25rem",
                }}
              >
                DID Information
              </h4>
              <Row>
                <Col md={6}>
                  <Field label="Number" value={did?.number} required />
                </Col>
                <Col md={6}>
                  <Field
                    label="Translated Number"
                    value={did?.translated_number}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Field label="Rate Center" value={did?.rate_center} />
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label style={labelStyle}>Port Status</Form.Label>
                    <div
                      style={{
                        padding: "0.5rem 0.75rem",
                        borderRadius: "0.375rem",
                        border: `1px solid ${isDark ? "#374151" : "#dee2e6"}`,
                        backgroundColor: isDark ? "#111827" : "#f8f9fa",
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {getPortBadge()}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Field label="Label" value={did?.label} />
                </Col>
                <Col md={6}>
                  <Field
                    label="Additional Label"
                    value={did?.additional_label}
                  />
                </Col>
              </Row>

              {/* ── Section 2: 911 Information ── */}
              <h4
                style={{
                  fontWeight: 500,
                  color: isDark ? "#93c5fd" : "#1e3a5f",
                  borderBottom: `1px solid ${isDark ? "#374151" : "#dee2e6"}`,
                  paddingBottom: "0.5rem",
                  marginBottom: "1.25rem",
                  marginTop: "1.5rem",
                }}
              >
                911 Information
              </h4>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>First Name</Form.Label>
                    <Input
                      value={e911?.first_name || ""}
                      onChange={(e) => updateE911("first_name", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Last Name</Form.Label>
                    <Input
                      value={e911?.last_name || ""}
                      onChange={(e) => updateE911("last_name", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Street Number</Form.Label>
                    <Input
                      value={e911?.street_number || ""}
                      onChange={(e) =>
                        updateE911("street_number", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Street Name</Form.Label>
                    <Input
                      value={e911?.street_name || ""}
                      onChange={(e) =>
                        updateE911("street_name", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Suite Number</Form.Label>
                    <Input
                      value={e911?.suite_number || ""}
                      onChange={(e) =>
                        updateE911("suite_number", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label>City</Form.Label>
                    <Input
                      value={e911?.city || ""}
                      onChange={(e) => updateE911("city", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Province</Form.Label>
                    <Input
                      value={e911?.province || ""}
                      onChange={(e) => updateE911("province", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Postal Code</Form.Label>
                    <Input
                      value={e911?.postal_code || ""}
                      onChange={(e) =>
                        updateE911("postal_code", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Other</Form.Label>
                    <Input
                      value={e911?.other || ""}
                      onChange={(e) => updateE911("other", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* ── Section 3: Call Statistics ── */}
              <h4
                style={{
                  fontWeight: 500,
                  color: isDark ? "#93c5fd" : "#1e3a5f",
                  borderBottom: `1px solid ${isDark ? "#374151" : "#dee2e6"}`,
                  paddingBottom: "0.5rem",
                  marginBottom: "1.25rem",
                  marginTop: "1.5rem",
                }}
              >
                Call Statistics — Since {month} 1st
              </h4>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.875rem",
                    color: isDark ? "#cbd5e1" : "#374151",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: isDark ? "#1f2937" : "#e9ecef",
                        color: isDark ? "#e2e8f0" : "#1e3a5f",
                      }}
                    >
                      {[
                        "# of Calls",
                        "Total Call Time",
                        "# of NA Calls",
                        "NA Call Time",
                        "# of Intl Calls",
                        "Intl Call Time",
                        "Intl Call Cost",
                      ].map((heading) => (
                        <th
                          key={heading}
                          style={{
                            padding: "0.75rem 1rem",
                            textAlign: "center",
                            fontWeight: 600,
                            border: `1px solid ${isDark ? "#374151" : "#dee2e6"}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      style={{
                        backgroundColor: isDark ? "#111827" : "#f8f9fa",
                      }}
                    >
                      {[
                        did?.intl_data?.total_calls,
                        secondsToHMS(did?.intl_data?.total_duration),
                        did?.intl_data?.na_call_count,
                        secondsToHMS(did?.intl_data?.na_total_duration),
                        did?.intl_data?.intl_call_count,
                        secondsToHMS(did?.intl_data?.intl_total_duration),
                        mnyfmt(did?.intl_data?.intl_cost),
                      ].map((val, i) => (
                        <td
                          key={i}
                          style={{
                            padding: "0.75rem 1rem",
                            textAlign: "center",
                            border: `1px solid ${isDark ? "#374151" : "#dee2e6"}`,
                            color: isDark ? "#9ca3af" : "#6b7280",
                          }}
                        >
                          {val ?? "—"}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-4 d-flex justify-content-between">
                <Button variant="danger" onClick={() => navigate(-1)}>
                  Back
                </Button>
                <Button variant="success" onClick={updateEmergency}>
                  Save
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default DIDProfile;
